import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface TestPassRateTrendProps {
  data: Array<{
    weekStart: string;
    weekLabel: string;
    avgTestPassRate: number | null;
    issueCount: number;
  }>;
  targetThreshold?: number;
  isLoading?: boolean;
}

export function TestPassRateTrendChart({
  data,
  targetThreshold = 95,
  isLoading = false,
}: TestPassRateTrendProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-64 mt-1" />
        </CardHeader>
        <CardContent className="h-80">
          <div className="flex items-center justify-center h-full">
            <Skeleton className="h-64 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Filter to only include weeks with pass rate data
  const chartData = data
    .filter((d) => d.avgTestPassRate !== null)
    .map((d) => ({
      ...d,
      avgTestPassRate: d.avgTestPassRate ?? 0,
    }));

  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Test Pass Rate Trends</CardTitle>
          <CardDescription>Average test pass rate over time (target: {targetThreshold}%)</CardDescription>
        </CardHeader>
        <CardContent className="h-80">
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <p className="text-sm">No test pass rate data available</p>
            <p className="text-xs mt-1">Trends will appear after GATE-5 runs</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="chart-test-pass-rate-trend">
      <CardHeader>
        <CardTitle>Test Pass Rate Trends</CardTitle>
        <CardDescription>Average test pass rate over time (target: {targetThreshold}%)</CardDescription>
      </CardHeader>
      <CardContent className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="passRateGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(142, 76%, 36%)" stopOpacity={0.8} />
                <stop offset="95%" stopColor="hsl(142, 76%, 36%)" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="weekLabel" />
            <YAxis domain={[0, 100]} />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "0.5rem",
              }}
              formatter={(value: number, name: string, props: any) => [
                `${value.toFixed(1)}% (${props.payload.issueCount} issues)`,
                "",
              ]}
              labelFormatter={(label) => `Week ${label}`}
            />
            <ReferenceLine
              y={targetThreshold}
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="5 5"
              label={{
                value: `Target: ${targetThreshold}%`,
                position: "right",
                fill: "hsl(var(--muted-foreground))",
                fontSize: 12,
              }}
            />
            <Area
              type="monotone"
              dataKey="avgTestPassRate"
              stroke="hsl(142, 76%, 36%)"
              strokeWidth={2}
              fill="url(#passRateGradient)"
              name="Avg Pass Rate"
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
